import React, { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, Send, CheckCircle2, Trash2, Pencil, DraftingCompass } from "lucide-react";

interface Props {
  projectId: string;
}

type Status = "draft" | "submitted" | "needs_completion" | "accepted";

interface Submission {
  id: string;
  discipline: string;
  company: string | null;
  contact_name: string | null;
  contact_email: string | null;
  status: Status;
  residual_risks_construction: string | null;
  residual_risks_use: string | null;
  assumptions: string | null;
  basp_comment: string | null;
  submitted_at: string | null;
}

const DISCIPLINES = [
  "Arkitekt (A)",
  "Konstruktion (K)",
  "VVS (V)",
  "El (E)",
  "Mark (M)",
  "Brand (BR)",
  "Geoteknik (G)",
  "Styr och övervakning (S)",
  "Övrigt",
];

const STATUS_LABELS: Record<Status, string> = {
  draft: "Utkast",
  submitted: "Inlämnad",
  needs_completion: "Behöver kompletteras",
  accepted: "Godkänd av Bas-P",
};

const STATUS_VARIANT: Record<Status, "outline" | "secondary" | "destructive" | "default"> = {
  draft: "outline",
  submitted: "secondary",
  needs_completion: "destructive",
  accepted: "default",
};

const emptyForm = {
  discipline: DISCIPLINES[0],
  company: "",
  contact_name: "",
  contact_email: "",
  residual_risks_construction: "",
  residual_risks_use: "",
  assumptions: "",
};

export const DesignerSubmissions: React.FC<Props> = ({ projectId }) => {
  const [rows, setRows] = useState<Submission[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState({ ...emptyForm });

  const load = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("designer_submissions")
      .select("*")
      .eq("project_id", projectId)
      .order("created_at", { ascending: true });
    if (error) toast.error("Kunde inte hämta projektörsinlämningar");
    setRows((data || []) as unknown as Submission[]);
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const startNew = () => {
    setEditingId(null);
    setForm({ ...emptyForm });
    setOpen(true);
  };

  const startEdit = (r: Submission) => {
    setEditingId(r.id);
    setForm({
      discipline: r.discipline,
      company: r.company || "",
      contact_name: r.contact_name || "",
      contact_email: r.contact_email || "",
      residual_risks_construction: r.residual_risks_construction || "",
      residual_risks_use: r.residual_risks_use || "",
      assumptions: r.assumptions || "",
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.discipline.trim()) {
      toast.error("Välj disciplin");
      return;
    }
    const payload = { ...form, project_id: projectId };
    const { error } = editingId
      ? await supabase.from("designer_submissions").update(payload).eq("id", editingId)
      : await supabase.from("designer_submissions").insert(payload);
    if (error) {
      toast.error("Kunde inte spara");
      return;
    }
    toast.success("Sparat");
    setOpen(false);
    load();
  };

  const setStatus = async (r: Submission, status: Status) => {
    const patch: {
      status: Status;
      submitted_at?: string;
      reviewed_at?: string;
    } = { status };
    if (status === "submitted") patch.submitted_at = new Date().toISOString();
    if (status === "accepted" || status === "needs_completion")
      patch.reviewed_at = new Date().toISOString();
    const { error } = await supabase.from("designer_submissions").update(patch).eq("id", r.id);
    if (error) {
      toast.error("Kunde inte uppdatera status");
      return;
    }
    load();
  };

  const remove = async (id: string) => {
    const { error } = await supabase.from("designer_submissions").delete().eq("id", id);
    if (error) {
      toast.error("Kunde inte ta bort");
      return;
    }
    load();
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 pb-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-lg">
              <DraftingCompass className="h-5 w-5 text-muted-foreground" />
              Dok 16 · Projektörsinlämning
            </CardTitle>
            <p className="mt-1 text-sm text-muted-foreground">
              Varje projektör redovisar de risker som inte gick att projektera bort – för byggskedet
              och för bruksskedet – samt vilka förutsättningar som gäller. Bas-P kvitterar
              inlämningen och för in riskerna i risksammanställningen och AMP.
            </p>
          </div>
          <Button onClick={startNew} className="gap-1">
            <Plus className="h-4 w-4" /> Ny inlämning
          </Button>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-sm text-muted-foreground">Laddar…</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Inga inlämningar ännu. Lägg upp en per disciplin (A, K, V, E, M …).
            </p>
          ) : (
            <div className="space-y-3">
              {rows.map((r) => (
                <div key={r.id} className="rounded-lg border p-3">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{r.discipline}</span>
                    {r.company && (
                      <span className="text-sm text-muted-foreground">· {r.company}</span>
                    )}
                    <Badge variant={STATUS_VARIANT[r.status]} className="ml-auto">
                      {STATUS_LABELS[r.status]}
                    </Badge>
                  </div>
                  {(r.contact_name || r.contact_email) && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      {[r.contact_name, r.contact_email].filter(Boolean).join(" · ")}
                    </p>
                  )}
                  <div className="mt-2 grid gap-2 sm:grid-cols-2">
                    <div>
                      <p className="text-xs font-medium text-muted-foreground">
                        Kvarvarande risker – byggskede
                      </p>
                      <p className="whitespace-pre-wrap text-sm">
                        {r.residual_risks_construction || "–"}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs font-medium text-muted-foreground">
                        Kvarvarande risker – bruksskede
                      </p>
                      <p className="whitespace-pre-wrap text-sm">{r.residual_risks_use || "–"}</p>
                    </div>
                  </div>
                  {r.assumptions && (
                    <div className="mt-2">
                      <p className="text-xs font-medium text-muted-foreground">Förutsättningar</p>
                      <p className="whitespace-pre-wrap text-sm">{r.assumptions}</p>
                    </div>
                  )}
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button size="sm" variant="ghost" className="gap-1" onClick={() => startEdit(r)}>
                      <Pencil className="h-4 w-4" /> Redigera
                    </Button>
                    {r.status === "draft" || r.status === "needs_completion" ? (
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1"
                        onClick={() => setStatus(r, "submitted")}
                      >
                        <Send className="h-4 w-4" /> Lämna in till Bas-P
                      </Button>
                    ) : null}
                    {r.status === "submitted" && (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          className="gap-1"
                          onClick={() => setStatus(r, "accepted")}
                        >
                          <CheckCircle2 className="h-4 w-4" /> Godkänn
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setStatus(r, "needs_completion")}
                        >
                          Begär komplettering
                        </Button>
                      </>
                    )}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="ml-auto gap-1 text-destructive"
                      onClick={() => remove(r.id)}
                    >
                      <Trash2 className="h-4 w-4" /> Ta bort
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingId ? "Redigera inlämning" : "Ny projektörsinlämning"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label>Disciplin</Label>
              <Select
                value={form.discipline}
                onValueChange={(v) => setForm((f) => ({ ...f, discipline: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DISCIPLINES.map((d) => (
                    <SelectItem key={d} value={d}>
                      {d}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1">
                <Label>Företag</Label>
                <Input
                  value={form.company}
                  onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
                />
              </div>
              <div className="space-y-1">
                <Label>Kontaktperson</Label>
                <Input
                  value={form.contact_name}
                  onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))}
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label>E-post</Label>
              <Input
                type="email"
                value={form.contact_email}
                onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label>Kvarvarande risker – byggskede</Label>
              <Textarea
                rows={4}
                placeholder="Risker som inte gick att projektera bort och som Bas-U måste hantera."
                value={form.residual_risks_construction}
                onChange={(e) =>
                  setForm((f) => ({ ...f, residual_risks_construction: e.target.value }))
                }
              />
            </div>
            <div className="space-y-1">
              <Label>Kvarvarande risker – bruksskede</Label>
              <Textarea
                rows={4}
                placeholder="Risker vid drift, underhåll och framtida arbeten i byggnadsverket."
                value={form.residual_risks_use}
                onChange={(e) => setForm((f) => ({ ...f, residual_risks_use: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label>Förutsättningar och antaganden</Label>
              <Textarea
                rows={3}
                value={form.assumptions}
                onChange={(e) => setForm((f) => ({ ...f, assumptions: e.target.value }))}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>
              Avbryt
            </Button>
            <Button onClick={save}>Spara</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DesignerSubmissions;
